import { defineChain } from "viem";
import {
  Chain,
  arbitrum as viemArbitrum,
  avalanche as viemAvalanche,
  avalancheFuji as viemAvalancheFuji,
} from "viem/chains";
import type { GasLimitsConfig } from "types/fees";
import { getEnv } from "utils/env";

export const ETH_MAINNET = 1;
export const ARBITRUM = 42161;
export const AVALANCHE = 43114;
export const AVALANCHE_FUJI = 43113;
export const BOTANIX = 3637;
export const ASSETCHAIN_TESTNET = 42421;
export const ASSETCHAIN = 42420;
export const BASE = 8453;
export const BASE_SEPOLIA = 84532;
export const POLYGON = 137;
export const ARBITRUM_SEPOLIA = 421614;
export const BSC = 56;
export const BSC_TESTNET = 97;
export const BITLAYER = 200901;
export const BITLAYER_TESTNET = 200810;

/** Default public RPC URLs for chains. Used by SDK only; app may override via env. */
const DEFAULT_RPC_URLS: Record<number, string> = {
  [ARBITRUM]: "https://arb1.arbitrum.io/rpc",
  [BASE]: "https://mainnet.base.org",
  [BASE_SEPOLIA]: "https://sepolia.base.org",
  [POLYGON]: "https://polygon-rpc.com",
  [ARBITRUM_SEPOLIA]: "https://sepolia-rollup.arbitrum.io/rpc",
  [BSC]: "https://bsc-dataseed.binance.org",
  [BSC_TESTNET]: "https://data-seed-prebsc-1-s1.binance.org:8545",
  [BITLAYER]: "https://rpc.bitlayer.org",
  [BITLAYER_TESTNET]: "https://testnet-rpc.bitlayer.org",
};

function getDefaultRpcUrl(chainId: number): string {
  return DEFAULT_RPC_URLS[chainId]
}

export type LegacyContractsChain = typeof ARBITRUM | typeof AVALANCHE | typeof AVALANCHE_FUJI;
export type UiContractsChain = typeof BOTANIX | typeof ASSETCHAIN | typeof ASSETCHAIN_TESTNET | LegacyContractsChain;
export type UiSupportedChain = typeof BOTANIX | typeof ASSETCHAIN | typeof ASSETCHAIN_TESTNET;

export const SUPPORTED_CHAIN_IDS: UiSupportedChain[] = [ASSETCHAIN, ASSETCHAIN_TESTNET];
export const SUPPORTED_CHAIN_IDS_DEV: UiSupportedChain[] = [...SUPPORTED_CHAIN_IDS];

const LEGACY_CHAIN_NAMES_MAP: Record<number, string> = {
  [ARBITRUM]: "Arbitrum",
  [AVALANCHE]: "Avalanche",
  [AVALANCHE_FUJI]: "Avalanche Fuji",
};

const LEGACY_HIGH_EXECUTION_FEES_MAP: Record<number, number> = {
  [ARBITRUM]: 5,
  [AVALANCHE]: 5,
  [AVALANCHE_FUJI]: 5,
};

const LEGACY_EXCESSIVE_EXECUTION_FEES_MAP: Record<number, number> = {
  [ARBITRUM]: 10,
  [AVALANCHE]: 10,
  [AVALANCHE_FUJI]: 10,
};

const LEGACY_MIN_EXECUTION_FEE_USD: Record<number, bigint | undefined> = {
  [ARBITRUM]: undefined,
  [AVALANCHE]: undefined,
  [AVALANCHE_FUJI]: undefined,
};

const LEGACY_CONSTANTS: Record<
  number,
  {
    nativeTokenSymbol: string;
    wrappedTokenSymbol: string;
    defaultCollateralSymbol: string;
    defaultFlagOrdersEnabled: boolean;
    positionReaderPropsLength: number;
    v2: boolean;
    SWAP_ORDER_EXECUTION_GAS_FEE: bigint;
    INCREASE_ORDER_EXECUTION_GAS_FEE: bigint;
    DECREASE_ORDER_EXECUTION_GAS_FEE: bigint;
  }
> = {
  [ARBITRUM]: {
    nativeTokenSymbol: "ETH",
    wrappedTokenSymbol: "WETH",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: 25_000_000_000_000n,
    INCREASE_ORDER_EXECUTION_GAS_FEE: 25_000_000_000_000n,
    DECREASE_ORDER_EXECUTION_GAS_FEE: 26_000_000_000_000n,
  },
  [AVALANCHE]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: 2_000_000_000_000_000n,
    INCREASE_ORDER_EXECUTION_GAS_FEE: 2_000_000_000_000_000n,
    DECREASE_ORDER_EXECUTION_GAS_FEE: 2_100_000_000_000_000n,
  },
  [AVALANCHE_FUJI]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: 2_000_000_000_000_000n,
    INCREASE_ORDER_EXECUTION_GAS_FEE: 2_000_000_000_000_000n,
    DECREASE_ORDER_EXECUTION_GAS_FEE: 2_100_000_000_000_000n,
  },
};

const LEGACY_VIEM_CHAIN_BY_CHAIN_ID: Record<number, Chain> = {
  [ARBITRUM]: viemArbitrum,
  [AVALANCHE]: viemAvalanche,
  [AVALANCHE_FUJI]: viemAvalancheFuji,
};

export const CHAIN_NAMES_MAP: Record<number, string> = {
  [BOTANIX]: "Botanix",
  [ASSETCHAIN]: "AssetChain",
  [ASSETCHAIN_TESTNET]: "AssetChain Testnet",
};
Object.assign(CHAIN_NAMES_MAP, LEGACY_CHAIN_NAMES_MAP);

export const HIGH_EXECUTION_FEES_MAP: Record<number, number> = {
  [BOTANIX]: 5, // 5 USD
  [ASSETCHAIN]: 5, // 5 USD
  [ASSETCHAIN_TESTNET]: 5, // 5 USD
};
Object.assign(HIGH_EXECUTION_FEES_MAP, LEGACY_HIGH_EXECUTION_FEES_MAP);

// added to maxPriorityFeePerGas
// applied to EIP-1559 transactions only
// is not applied to execution fee calculation
export const MAX_FEE_PER_GAS_MAP: Record<number, bigint> = {
  [BOTANIX]: 20n,
};

// added to maxPriorityFeePerGas
// applied to EIP-1559 transactions only
// is also applied to the execution fee calculation
export const GAS_PRICE_PREMIUM_MAP: Record<number, bigint> = {};

/*
  that was a constant value in ethers v5, after ethers v6 migration we use it as a minimum for maxPriorityFeePerGas
*/
export const MAX_PRIORITY_FEE_PER_GAS_MAP: Record<UiSupportedChain, bigint | undefined> = {
  [BOTANIX]: 7n,
  [ASSETCHAIN]: 1500000000n,
  [ASSETCHAIN_TESTNET]: 1500000000n,
};

export const EXCESSIVE_EXECUTION_FEES_MAP: Record<UiSupportedChain, number> = {
  [BOTANIX]: 10, // 10 USD
  [ASSETCHAIN]: 10, // 10 USD
  [ASSETCHAIN_TESTNET]: 10, // 10 USD
};
Object.assign(EXCESSIVE_EXECUTION_FEES_MAP as Record<number, number>, LEGACY_EXCESSIVE_EXECUTION_FEES_MAP);

// Minimum execution fee in the chain's native token (wei). This avoids relying on token price feeds
// (which may be unavailable in dev/testnet) and matches how contracts validate execution fee amounts.
export const MIN_EXECUTION_FEE_TOKEN: Record<UiSupportedChain, bigint | undefined> = {
  [BOTANIX]: undefined,
  // Keep AssetChain above the contract-required execution fee at current testnet gas prices.
  [ASSETCHAIN]: 20_000_000_000_000_000n,
  [ASSETCHAIN_TESTNET]: 20_000_000_000_000_000n,
};

// avoid botanix gas spikes when chain is not actively used
// if set, execution fee value should not be less than this in USD equivalent
export const MIN_EXECUTION_FEE_USD: Record<UiSupportedChain, bigint | undefined> = {
  [BOTANIX]: 1000000000000000000000000000n, // 1e27 $0.001
  [ASSETCHAIN]: undefined,
  [ASSETCHAIN_TESTNET]: undefined,
};
(MIN_EXECUTION_FEE_USD as Record<number, bigint | undefined>)[ARBITRUM] = LEGACY_MIN_EXECUTION_FEE_USD[ARBITRUM];
(MIN_EXECUTION_FEE_USD as Record<number, bigint | undefined>)[AVALANCHE] = LEGACY_MIN_EXECUTION_FEE_USD[AVALANCHE];
(MIN_EXECUTION_FEE_USD as Record<number, bigint | undefined>)[AVALANCHE_FUJI] =
  LEGACY_MIN_EXECUTION_FEE_USD[AVALANCHE_FUJI];

// added to gasPrice
// applied to *non* EIP-1559 transactions only
//
// it is *not* applied to the execution fee calculation, and in theory it could cause issues
// if gas price used in the execution fee calculation is lower
// than the gas price used in the transaction (e.g. create order transaction)
// then the transaction will fail with InsufficientExecutionFee error.
//
// it doesn't make much sense to set this buffer higher than the execution fee buffer
// because if the paid gas price is higher than the gas price used in the execution fee calculation
// and the transaction will still fail with InsufficientExecutionFee
//
// this buffer could also cause issues on a blockchain that uses passed gas price
// especially if execution fee buffer and lower than gas price buffer defined bellow
export const GAS_PRICE_BUFFER_MAP: Record<number, bigint> = {};

export const botanix: Chain = defineChain({
  id: BOTANIX,
  name: "Botanix",
  nativeCurrency: {
    name: "Bitcoin",
    symbol: "BTC",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [
        // this rpc returns incorrect gas price
        // "https://rpc.botanixlabs.com",

        "https://rpc.ankr.com/botanix_mainnet",
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "BotanixScan",
      url: "https://botanixscan.io",
    },
  },
  contracts: {
    multicall3: {
      address: "0x4BaA24f93a657f0c1b4A0Ffc72B91011E35cA46b",
    },
  },
});

export const assetChainTestnet: Chain = defineChain({
  id: ASSETCHAIN_TESTNET,
  name: "AssetChain Testnet",
  nativeCurrency: {
    name: "RWA",
    symbol: "RWA",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [
        // If VITE_ASSETCHAIN_TESTNET_RPC is set, use it directly (e.g. a public RPC node);
        // otherwise fall back to the backend RPC proxy.
        getEnv("VITE_ASSETCHAIN_TESTNET_RPC", "https://enugu-rpc.assetchain.org") ||
        `${getEnv("VITE_BACKEND_URL", "https://nectarbackend-boo8.onrender.com")}/rpc/assetchain-testnet`,
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "AssetChain Explorer",
      url: "https://scan-testnet.assetchain.org",
    },
  },
  contracts: {
    multicall3: {
      address: "0xc5c2340bCe4C4C0b806De8de194e0BE3129a93B4",
    },
  },
  testnet: true,
});

export const assetChain: Chain = defineChain({
  id: ASSETCHAIN,
  name: "AssetChain",
  nativeCurrency: {
    name: "RWA",
    symbol: "RWA",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [
        // If VITE_ASSETCHAIN_RPC is set, use it directly (e.g. mainnet-rpc.assetchain.org);
        // otherwise fall back to the backend RPC proxy.
        getEnv("VITE_ASSETCHAIN_RPC", "https://mainnet-rpc.assetchain.org") ||
        `${getEnv("VITE_BACKEND_URL", "https://nectarbackend-boo8.onrender.com")}/rpc/assetchain`,
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "AssetChain Explorer",
      url: "https://scan.assetchain.org",
    },
  },
  contracts: {
    multicall3: {
      address: "0x2ae7d90C1cc0C7cf70361EcAaede33D33022215A",
    },
  },
  testnet: false,
});

export const arbitrum: Chain = defineChain({
  id: ARBITRUM,
  name: "Arbitrum",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(ARBITRUM)],
    },
  },
  blockExplorers: {
    default: {
      name: "Arbitrum Explorer",
      url: "https://arbiscan.io",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
});

export const base: Chain = defineChain({
  id: BASE,
  name: "Base",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BASE)],
    },
  },
  blockExplorers: {
    default: {
      name: "Base Explorer",
      url: "https://basescan.org",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
  testnet: false,
});

export const baseSepolia: Chain = defineChain({
  id: BASE_SEPOLIA,
  name: "Base Sepolia",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BASE_SEPOLIA)],
    },
  },
  blockExplorers: {
    default: {
      name: "Base Sepolia Explorer",
      url: "https://basescan.org",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
  testnet: true,
});
export const polygon: Chain = defineChain({
  id: POLYGON,
  name: "Polygon",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(POLYGON)],
    },
  },
  blockExplorers: {
    default: {
      name: "Polygon Explorer",
      url: "https://polygonscan.com",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
  testnet: false,
});

export const arbitrumSepolia: Chain = defineChain({
  id: ARBITRUM_SEPOLIA,
  name: "Arbitrum Sepolia",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(ARBITRUM_SEPOLIA)],
    },
  },
  blockExplorers: {
    default: {
      name: "Arbitrum Sepolia Explorer",
      url: "https://arbiscan.io",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
});

export const bsc: Chain = defineChain({
  id: BSC,
  name: "BSC",
  nativeCurrency: {
    name: "BNB",
    symbol: "BNB",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BSC)],
    },
  },
  blockExplorers: {
    default: {
      name: "BSC Explorer",
      url: "https://bscscan.com",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
});

export const bscTestnet: Chain = defineChain({
  id: BSC_TESTNET,
  name: "BSC Testnet",
  nativeCurrency: {
    name: "BNB",
    symbol: "BNB",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BSC_TESTNET)],
    },
  },
  blockExplorers: {
    default: {
      name: "BSC Testnet Explorer",
      url: "https://testnet.bscscan.com",
    },
  },
  contracts: {
    multicall3: {
      address: "0xc5c2340bCe4C4C0b806De8de194e0BE3129a93B4",
    },
  },
  testnet: true,
});

export const bitlayer: Chain = defineChain({
  id: BITLAYER,
  name: "Bitlayer",
  nativeCurrency: {
    name: "Bitlayer",
    symbol: "BTC",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BITLAYER)],
    },
  },
  blockExplorers: {
    default: {
      name: "Bitlayer Explorer",
      url: "https://www.btrscan.com/",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
});

export const bitlayerTestnet: Chain = defineChain({
  id: BITLAYER_TESTNET,
  name: "Bitlayer Testnet",
  nativeCurrency: {
    name: "Bitlayer",
    symbol: "BTC",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [getDefaultRpcUrl(BITLAYER_TESTNET)],
    },
  },
  blockExplorers: {
    default: {
      name: "Bitlayer Testnet Explorer",
      url: "https://testnet.btrscan.com/",
    },
  },
  contracts: {
    multicall3: {
      address: "0x8a990000626a4b166f1632d0a0e9022294370b23",
    },
  },
  testnet: true,
});

const VIEM_CHAIN_BY_CHAIN_ID: Record<UiSupportedChain, Chain> = {
  [BOTANIX]: botanix,
  [ASSETCHAIN]: assetChain,
  [ASSETCHAIN_TESTNET]: assetChainTestnet,
};

export function getChainName(chainId: number) {
  return CHAIN_NAMES_MAP[chainId] ?? LEGACY_CHAIN_NAMES_MAP[chainId];
}

export const getViemChain = (chainId: number): Chain => {
  return VIEM_CHAIN_BY_CHAIN_ID[chainId] ?? LEGACY_VIEM_CHAIN_BY_CHAIN_ID[chainId];
};

export function getHighExecutionFee(chainId: number) {
  return HIGH_EXECUTION_FEES_MAP[chainId] ?? LEGACY_HIGH_EXECUTION_FEES_MAP[chainId] ?? 5;
}

export function getExcessiveExecutionFee(chainId: number) {
  return EXCESSIVE_EXECUTION_FEES_MAP[chainId] ?? LEGACY_EXCESSIVE_EXECUTION_FEES_MAP[chainId] ?? 10;
}

export function isSupportedChain(chainId: number, dev = false) {
  return (dev ? SUPPORTED_CHAIN_IDS_DEV : SUPPORTED_CHAIN_IDS).includes(chainId as UiSupportedChain);
}

export const EXECUTION_FEE_CONFIG_V2: {
  [chainId in UiSupportedChain]: {
    shouldUseMaxPriorityFeePerGas: boolean;
    defaultBufferBps?: number;
  };
} = {
  [BOTANIX]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 3000, // 30%
  },
  [ASSETCHAIN]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 1000, // 10%
  },
  [ASSETCHAIN_TESTNET]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 1000, // 10%
  },
};

const DEFAULT_STATIC_GAS_LIMITS: Partial<GasLimitsConfig> = {
  depositToken: 0n,
  withdrawalMultiToken: 0n,
  shift: 0n,
  singleSwap: 50_000n,
  swapOrder: 100_000n,
  increaseOrder: 1_000_000n,
  decreaseOrder: 900_000n,
  estimatedGasFeeBaseAmount: 200n,
  estimatedGasFeePerOraclePrice: 20n,
  estimatedFeeMultiplierFactor: 1n,
  gelatoRelayFeeMultiplierFactor: 0n,
  glvDepositGasLimit: 0n,
  glvWithdrawalGasLimit: 0n,
  glvPerMarketGasLimit: 0n,
  createOrderGasLimit: 1_000_000n,
  updateOrderGasLimit: 800_000n,
  cancelOrderGasLimit: 700_000n,
  tokenPermitGasLimit: 90_000n,
};

export const GAS_LIMITS_STATIC_CONFIG: Record<UiSupportedChain, Partial<GasLimitsConfig>> = {
  [BOTANIX]: {
    ...DEFAULT_STATIC_GAS_LIMITS,
  },
  [ASSETCHAIN]: {
    ...DEFAULT_STATIC_GAS_LIMITS,
  },
  [ASSETCHAIN_TESTNET]: {
    ...DEFAULT_STATIC_GAS_LIMITS,
    depositToken: 800_000n,
    withdrawalMultiToken: 800_000n,
  },
};
